import { Planable } from "./planableModel";
import { DEFAULT_PERIOD_OBJECT } from "./learningObjectivesConstants";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { periodManager } from "models/learning-objectives/periodManager";
import { cloneDeep, isEqual } from "lodash";

export class Period extends Planable {
  id;
  name;
  type;
  temporary;
  meta = DEFAULT_PERIOD_OBJECT.meta;

  constructor(data = {}) {
    super(data);

    this.id = data.id || this.generateTempId();
    this.name = data.name || DEFAULT_PERIOD_OBJECT.name;
    this.type = data.type || DEFAULT_PERIOD_OBJECT.type;
    this.temporary = data.temporary || DEFAULT_PERIOD_OBJECT.temporary;

    Object.assign(this.meta, data.meta);

    // planning meta data
    this.planningMeta.canDrag = true;
    this.planningMeta.manager = periodManager.value;

    this.updateInitialValue();
  }

  canDelete() {
    return this.meta.can_delete;
  }

  canUpdate() {
    return this.meta.can_update;
  }

  async deletePeriod() {
    if (!this.canDelete()) {
      return;
    }

    if (this.temporary) {
      return Promise.resolve();
    }

    const learningObjectivesStore = useLearningObjectivesStore();
    const { deletePeriod: useDeletePeriod } = learningObjectivesStore;

    await useDeletePeriod(this.id);
  }

  async updatePeriodInformation({ name, id }) {
    if (!this.canUpdate()) {
      return;
    }

    if (this.temporary) {
      Object.assign(this, { name });

      return Promise.resolve();
    }

    const learningObjectivesStore = useLearningObjectivesStore();
    const { updatePeriod: useUpdatePeriod } = learningObjectivesStore;

    // Hide meta from the update request
    const { planningMeta, ...rest } = this;

    const { data } = await useUpdatePeriod({ ...rest, name, id });

    Object.assign(this, data);
  }

  valuesToCheckForChanges() {
    // We remove some values that we don't want to check for changes
    const {
      planningMeta: {
        manager,
        initialDragState,
        groupIndex,
        ...planningMetaValuesToCheck
      },
      initialValue,
      ...periodValuesToCheck
    } = this;

    return Object.assign({}, periodValuesToCheck, {
      planningMeta: planningMetaValuesToCheck,
    });
  }

  updateInitialValue() {
    this.initialValue = cloneDeep(this.valuesToCheckForChanges());
  }

  hasChanges() {
    return !isEqual(this.initialValue, this.valuesToCheckForChanges());
  }

  async upsertPeriod() {
    if (!this.canUpdate() || !this.hasChanges()) {
      return;
    }

    const learningObjectivesStore = useLearningObjectivesStore();
    const { updatePeriod, storePeriod } = learningObjectivesStore;

    // Hide meta from the update request
    const { planningMeta, ...rest } = this;

    const { data } = this.temporary
      ? await storePeriod(rest)
      : await updatePeriod(rest);

    Object.assign(this, data, { temporary: false });
  }
}
